// src/pages/Leaderboard.js
import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';

// Custom Sheriff Star Component for Texas
const SheriffStar = ({ size = "w-4 h-4" }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L15.09 8.26L22 9L17 14.14L18.18 21L12 17.77L5.82 21L7 14.14L2 9L8.91 8.26L12 2Z"
      fill="#FFD700"
    />
  </svg>
);

function Leaderboard() {
  const [users, setUsers] = useState([]);
  const [races, setRaces] = useState([]);
  const [raceScores, setRaceScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define marquee races with their badge info
  const marqueeRaces = {
    'monaco': {
      name: 'Monaco GP',
      badge: '🎲',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      title: 'Monaco Dice',
      isCustom: false
    },
    'silverstone': {
      name: 'British GP', 
      badge: '⚔️',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      title: 'Silverstone Sword',
      isCustom: false
    },
    'spa': {
      name: 'Spa GP',
      badge: '🌲', 
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      title: 'Spa Forest',
      isCustom: false
    },
    'monza': {
      name: 'Monza GP',
      badge: '🏛️',
      color: 'text-green-600', 
      bgColor: 'bg-green-100',
      title: 'Monza Temple of Speed',
      isCustom: false
    },
    'cota': {
      name: 'COTA GP',
      badge: <SheriffStar size="w-4 h-4" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-800',
      title: 'Texas Sheriff',
      isCustom: true
    },
    'abu-dhabi': {
      name: 'Abu Dhabi GP',
      badge: '🐪',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100', 
      title: 'Abu Dhabi Desert Camel',
      isCustom: false
    }
  };

  // Helper function to check if predictions are open for a race (FIXED VERSION)
  const isPredictionsOpen = useCallback((race) => {
    if (!race) return false;
    
    const now = new Date();
    const raceDate = new Date(race.date);
    
    // If race date is in the past, predictions are closed
    if (raceDate < now) return false;
    
    // If qualifying has already happened, predictions are closed
    if (race.qualifyingDateTime) {
      const qualifyingTime = new Date(race.qualifyingDateTime);
      // FIX: Check prediction closing time (2 minutes before qualifying)
      const predictionClosingTime = new Date(qualifyingTime.getTime() - (2 * 60 * 1000));
      if (predictionClosingTime < now) return false;
    }
    
    return true;
  }, []);

  // Helper function to check if tips are closed (predictions were open but now closed)
  const areTipsClosed = useCallback((race) => {
    if (!race) return false;
    
    // Tips are closed if predictions are not open
    // This means either the race has happened or qualifying has started
    return !isPredictionsOpen(race);
  }, [isPredictionsOpen]);

  // Check if a race is a marquee race
  const getMarqueeRaceInfo = (raceName) => {
    const normalizedName = raceName.toLowerCase();
    
    console.log(`Checking race: "${raceName}" -> normalized: "${normalizedName}"`);
    
    if (normalizedName.includes('monaco')) {
      console.log('Matched Monaco');
      return marqueeRaces.monaco;
    }
    if (normalizedName.includes('british') || normalizedName.includes('silverstone')) {
      console.log('Matched British/Silverstone');
      return marqueeRaces.silverstone;
    }
    if (normalizedName.includes('belgian') || normalizedName.includes('belgium')) {
      console.log('Matched Belgian/Belgium');
      return marqueeRaces.spa;
    }
    if (normalizedName.includes('monza') || normalizedName.includes('italian')) {
      console.log('Matched Monza/Italian');
      return marqueeRaces.monza;
    }
    if (normalizedName.includes('austin') || normalizedName.includes('cota') || normalizedName.includes('united states')) {
      console.log('Matched Austin/COTA/United States');
      return marqueeRaces.cota;
    }
    if (normalizedName.includes('abu dhabi')) {
      console.log('Matched Abu Dhabi');
      return marqueeRaces['abu-dhabi'];
    }
    
    console.log('No match found');
    return null;
  };

  // Get user's badges based on race wins (SIMPLE VERSION - only for completed races)
  const getUserBadges = (userId) => {
    const badges = [];
    
    races.forEach(race => {
      const marqueeInfo = getMarqueeRaceInfo(race.raceName);
      
      // Debug: Log all marquee races and their status
      if (marqueeInfo) {
        console.log(`MARQUEE RACE: ${race.raceName}, Status: ${race.status}, Date: ${race.date}`);
      }
      
      // Only award badges for races that have status 'completed'
      if (marqueeInfo && race.status === 'completed') {
        console.log(`PROCESSING COMPLETED MARQUEE RACE: ${race.raceName}`);
        
        // Check if this user won this race
        if (raceScores[race._id]) {
          const raceResults = Object.entries(raceScores[race._id])
            .map(([uid, score]) => ({ userId: uid, score }))
            .sort((a, b) => b.score - a.score);
          
          // Check if this user won (highest score) and has score > 0
          if (raceResults.length > 0 && 
              raceResults[0].userId === userId && 
              raceResults[0].score > 0) {
            console.log(`AWARDING BADGE to user ${userId} for race ${race.raceName}`);
            badges.push(marqueeInfo);
          }
        }
      }
    });
    
    console.log(`User ${userId} total badges:`, badges.length);
    return badges;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 1. Fetch leaderboard data (users with total points)
        const leaderboardResponse = await apiClient.get('/api/scores/leaderboard');
        
        // 2. Fetch all races
        const racesResponse = await apiClient.get('/api/f1data/races/all');
        
        // Ensure race data is valid
        if (!Array.isArray(racesResponse.data)) {
          console.error('Race data is not an array:', racesResponse.data);
          setError('Invalid race data format');
          setLoading(false);
          return;
        }
        
        console.log(`Found ${racesResponse.data.length} total races`);
        
        // 3. Filter completed races
        const now = new Date();
        const completedRaces = racesResponse.data.filter(race => 
          race && (
            race.status === 'completed' || 
            (race.date && new Date(race.date) < now)
          )
        );
        
        console.log(`Found ${completedRaces.length} completed races`);
        
        // 4. Sort races by date (most recent first)
        completedRaces.sort((a, b) => {
          if (!a.date) return 1;
          if (!b.date) return -1;
          return new Date(b.date) - new Date(a.date);
        });
        
        // 5. Store users and races
        const userList = Array.isArray(leaderboardResponse.data) ? leaderboardResponse.data : [];
        setUsers(userList);
        setRaces(completedRaces);
        
        // 6. Fetch user predictions for each race to get points
        const scores = {};
        
        // For each race, try to get predictions
        for (const race of completedRaces) {
          try {
            // First create an empty scores object for this race
            scores[race._id] = {};
            
            // Try to get scores from the scores endpoint
            const raceScoreResponse = await apiClient.get(`/api/scores/race/${race._id}`);
            
            if (raceScoreResponse.data && raceScoreResponse.data.scores) {
              // Map scores by user ID for quick lookup
              raceScoreResponse.data.scores.forEach(score => {
                if (score.user && score.score > 0) { // Only include users with scores > 0
                  scores[race._id][score.user] = score.score;
                }
              });
            }
          } catch (scoreError) {
            console.log(`Could not fetch scores for race ${race._id}:`, scoreError);
            // Continue without scores for this race
          }
        }
        
        console.log('Collected race scores:', scores);
        setRaceScores(scores);
        setError(null);
      } catch (err) {
        setError('Failed to load data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to calculate user statistics
  const calculateUserStats = (userId) => {
    const userScores = [];
    let totalPoints = 0;
    let racesParticipated = 0;

    // Go through all races and collect this user's scores
    races.forEach(race => {
      const score = getUserRaceScore(userId, race._id);
      if (score > 0) {
        userScores.push(score);
        totalPoints += score;
        racesParticipated++;
      }
    });

    const averageScore = racesParticipated > 0 ? totalPoints / racesParticipated : 0;

    return {
      totalPoints,
      racesParticipated,
      averageScore: Math.round(averageScore * 100) / 100, // Round to 2 decimal places
      userScores
    };
  };

  // Helper function to get a user's score for a specific race
  const getUserRaceScore = (userId, raceId) => {
    // Check if we have scores for this race
    if (raceScores[raceId] && raceScores[raceId][userId] !== undefined) {
      return raceScores[raceId][userId];
    }
    
    return 0; // Default score if no data found
  };

  // Create enhanced user data with calculated stats and sort by average score
  const enhancedUsers = users.map(user => ({
    ...user,
    stats: calculateUserStats(user.id),
    badges: getUserBadges(user.id)
  })).sort((a, b) => {
    // Primary sort: by average score (descending)
    if (b.stats.averageScore !== a.stats.averageScore) {
      return b.stats.averageScore - a.stats.averageScore;
    }
    // Secondary sort: by races participated (descending) - more races = tie-breaker
    if (b.stats.racesParticipated !== a.stats.racesParticipated) {
      return b.stats.racesParticipated - a.stats.racesParticipated;
    }
    // Tertiary sort: by total points (descending)
    return b.stats.totalPoints - a.stats.totalPoints;
  });

  if (loading) {
    return <div className="text-center py-10">Loading leaderboard...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Leaderboard</h1>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[60px]">
                Rank
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[60px] bg-gray-50 z-10 min-w-[200px]">
                Username
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[260px] bg-gray-50 z-10 min-w-[80px]">
                Avg Score
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[340px] bg-gray-50 z-10 min-w-[80px]">
                Total Pts
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[420px] bg-gray-50 z-10 min-w-[70px]">
                Races
              </th>
              
              {/* Dynamic race columns */}
              {races.map(race => (
                <th key={race._id} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[60px]">
                  {race.circuit?.country || (race.raceName ? race.raceName.split(' ')[0] : 'Race')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {enhancedUsers.map((user, index) => (
              <tr key={user.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-inherit z-10 font-medium text-center">
                  {index + 1}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-[60px] bg-inherit z-10">
                  <div className="flex items-center space-x-2">
                    <span>{user.username}</span>
                    {/* Display badges */}
                    <div className="flex space-x-1">
                      {user.badges.map((badge, badgeIndex) => (
                        <div
                          key={badgeIndex}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}
                          title={`${badge.title} - ${badge.name} Winner`}
                        >
                          {badge.isCustom ? (
                            <span className="flex items-center">{badge.badge}</span>
                          ) : (
                            <span className="text-sm">{badge.badge}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-blue-600 sticky left-[260px] bg-inherit z-10 text-center">
                  {user.stats.averageScore.toFixed(1)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 sticky left-[340px] bg-inherit z-10 text-center">
                  {user.stats.totalPoints}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 sticky left-[420px] bg-inherit z-10 text-center">
                  {user.stats.racesParticipated}
                </td>
                
                {/* Display race scores */}
                {races.map(race => {
                  const points = getUserRaceScore(user.id, race._id);
                  
                  return (
                    <td key={`${user.id}-${race._id}`} className="px-3 py-4 whitespace-nowrap text-sm text-center">
                      <span className={`font-medium ${points > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {points > 0 ? points : '-'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend/Info section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Marquee Race Winner Badge Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-blue-700">
          {Object.entries(marqueeRaces).map(([key, badge]) => (
            <div key={key} className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}>
                {badge.isCustom ? (
                  <span className="flex items-center">{badge.badge}</span>
                ) : (
                  <span className="text-sm">{badge.badge}</span>
                )}
              </span>
              <span>{badge.title}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 text-xs text-blue-600">
          <p><strong>Ranking System:</strong> Average score → Races participated → Total points</p>
          <p>Badges are awarded to winners of marquee races (Monaco, Silverstone, Spa, Monza, COTA, Abu Dhabi)</p>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;